<template>
    <div>
        <Header />
        <div class="loader" style="z-index: 11000;" v-if="loading && !uploading">
            <q-spinner-gears size="50px" color="black" />
        </div>
        <q-banner v-if="success" class="app-banner app-banner--success" dense rounded>
            Données enregistrées
        </q-banner>
        <q-banner v-if="saveError" class="app-banner app-banner--error" dense rounded>
            Erreur d'enregistrement
        </q-banner>
        <div>
            <div class="title">
                <div class="navTitle">
                    <WorkflowBreadcrumb :exhibitName="name" current="navigation" />
                </div>
                <div style="text-align: center;">
                    <div class="info-icon">
                        <q-btn flat round dense icon="info">
                            <q-popup-proxy ref="popup">
                                <q-card>
                                    <q-card-section>
                                        Pour naviguer dans votre exposition, créer une trame à l'aide des points
                                        interactifs, choisissez leur position en les faisant glisser sur votre
                                        illustration
                                        de fonds
                                    </q-card-section>
                                </q-card>
                            </q-popup-proxy>
                        </q-btn>
                    </div>
                </div>
            </div>

            <div class="navButtons">
                <q-btn color="pink-10" style="margin-left: 7%" @click="navigateBack">Retour</q-btn>
                <div>
                    <q-btn label="Prévisualiser l'exposition en ligne" color="blue-grey-4" @click="openNewTab" />
                </div>
                <q-btn label="Page suivante" style="margin-right: 5%" color="deep-purple-11"
                    @click="navigateToResourceManagement"><q-tooltip>
                        Les points sont enregistrés automatiquement à chaque modification
                    </q-tooltip></q-btn>
            </div>

        </div>
        <hr style="margin-bottom: 15px" />
        
            
        
        <div class="grid-container">
            
            <div class="column column-1" :class="{ 'hidden': !showFirstColumn }">
                <h4><q-icon name="cloud_upload" /> Importer des fichiers pour la conception de la navigation</h4>
                <div
                    class="drop-zone"
                    :class="{ 'drop-zone--active': isDragging }"
                    @dragover.prevent="isDragging = true"
                    @dragenter.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="onDrop"
                >
                    <q-icon name="cloud_upload" size="48px" :color="isDragging ? 'primary' : 'grey-6'" />
                    <p class="q-mt-sm q-mb-xs" style="font-size: 14px;">
                        Glissez-déposez vos fichiers ici
                    </p>
                    <p class="text-caption text-grey-6 q-mb-sm">
                        (Images, Vidéos, Audios, Documents Word)
                    </p>
                    <q-file
                        v-model="selectedFiles"
                        label="ou cliquez pour sélectionner"
                        outlined
                        dense
                        multiple
                        accept="image/*,video/*,audio/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        @update:model-value="onFilesSelected"
                        style="max-width: 300px;"
                    >
                        <template v-slot:prepend>
                            <q-icon name="attach_file" />
                        </template>
                    </q-file>
                </div>

                <!-- Liste des fichiers sélectionnés -->
                <div v-if="filesToUpload.length > 0" class="q-mt-md">
                    <div class="text-subtitle2 q-mb-sm">{{ filesToUpload.length }} fichier(s) sélectionné(s)</div>
                    <q-list bordered separator class="rounded-borders q-mb-md">
                        <q-item v-for="(f, idx) in filesToUpload" :key="idx" class="q-pa-sm">
                            <q-item-section>
                                <div class="row items-center q-mb-xs">
                                    <q-icon :name="getFileIcon(f.file.name)" class="q-mr-sm" />
                                    <span class="text-weight-medium">{{ f.file.name }}</span>
                                    <q-space />
                                    <q-icon name="close" class="cursor-pointer" color="negative"
                                        @click="filesToUpload.splice(idx, 1)" />
                                </div>
                                <div class="row q-col-gutter-sm">
                                    <div class="col-6">
                                        <q-input v-model="f.title" label="Titre" dense outlined />
                                    </div>
                                    <div class="col-6">
                                        <q-input v-model="f.source" label="Nom de la source" dense outlined />
                                    </div>
                                    <div class="col-6">
                                        <q-input v-model="f.description" label="Description" dense outlined
                                            type="textarea" autogrow />
                                    </div>
                                    <div class="col-6">
                                        <q-input v-model="f.source_url" label="URL de la source" dense outlined />
                                    </div>
                                </div>
                            </q-item-section>
                        </q-item>
                    </q-list>
                    <q-btn @click="importFiles" color="secondary" label="Importer tous les fichiers" :loading="uploading" />
                </div>

                <!-- Progression -->
                <div v-if="uploading" class="q-mt-md">
                    <div class="text-caption q-mb-xs">
                        Fichier {{ uploadCurrent }} / {{ uploadTotal }}
                    </div>
                    <q-linear-progress :value="progress / 100" color="blue" />
                    <div class="text-caption text-center q-mt-xs">{{ progress }}%</div>
                </div>

                <div class="q-mt-md">
                    <q-btn label="Gérer les fichiers" color="warning" @click="resourcesList = true" />
                    <q-dialog v-model="resourcesList" maximized @hide="getResources">
                        <MediaManager :exhibitId="$store.state.exhibitId" />
                    </q-dialog>
                </div>

            </div>
            
            <div class="column column-2" :class="{ 'expanded': !showFirstColumn }">
                <div class="image-header-section">
                    <div class="toggle-column">
                        <q-btn
                            :label="showFirstColumn ? '◄' : '►'"
                            class="toggle-btn"
                            @click="toggleFirstColumn"
                            :class="{ 'collapsed': !showFirstColumn }"
                            color="grey-6"
                            size="sm"
                        >
                            <q-tooltip>
                                {{ showFirstColumn ? 'Masquer le panneau de fichiers' : 'Afficher le panneau de fichiers' }}
                            </q-tooltip>
                        </q-btn>
                    </div>
                    <h4 class="image-title" :class="{ 'title-expanded': !showFirstColumn }">
                        <q-icon name="image" /> Image principale
                    </h4>
                </div>
                <q-toggle v-model="hasTitle" color="pink" label="Afficher le nom des points"
                    @update:model-value="canvas.setShowLabels(hasTitle)" />
                <div id="imageContainer" class="container-section"></div>

            </div>
            <div class="column column-3">
                <div>
                    <h4><q-icon name="cloud_done" /> Choisir une image principale</h4>
                    <FileSelect style="width: 100%; margin-top: 5px;" :options="options" option-label="title" emit-value
                        map-options outlined v-model="model" label="Fichiers" @update:model-value="handleImageChange"
                        dense />
                    <br>
                    <q-btn color="secondary" label="Enregistrer" @click="saveMainResource"></q-btn>
                </div>
                <div>
                    <h4><q-icon name="add_location" /> Points de navigation</h4>
                    <q-btn color="pink-14" :disable="!mainResourceId || !model"
                        :label="addMode ? 'Annuler le placement' : '+ Ajouter un point'"
                        @click="toggleAddMode" />
                    <q-banner v-if="addMode" dense class="q-mt-sm bg-blue-1">
                        Cliquez sur l'image pour placer le nouveau point (Échap pour annuler).
                    </q-banner>
                    <PointEditorPanel v-if="selectedPoint" :point="selectedPoint" :options="options"
                        @save="onPanelSave" @cancel="onPanelCancel" @delete="confirmDeletePoint(selectedPoint)"
                        @preview="onPanelPreview" @dirty="panelDirty = $event" />
                    <div v-else class="text-grey-7 q-my-sm">
                        Cliquez sur un point de l'image (ou dans la liste) pour le modifier.
                    </div>
                </div>

                <div>
                    <h4><q-icon name="list" />Gérer les points</h4>
                    <q-list dense bordered padding class="rounded-borders">
                        <q-item v-for="point in points" :key="point.id" clickable
                            :active="point.id === selectedPointId" active-class="bg-blue-1 text-primary"
                            @click="trySelect(point.id)"
                            @mouseenter="canvas.highlightPoint(point.id, true)"
                            @mouseleave="canvas.highlightPoint(point.id, false)">
                            <q-item-section style="padding: 0px; border: none;">
                                {{ point.name }}
                            </q-item-section>
                            <q-item-section
                                style="display: flex; flex-direction: row; justify-content: end; padding: 0px; border: none;"
                                avatar>
                                <q-icon color="red" style="cursor: pointer;" name="delete"
                                    @click.stop="deletePoint(point.id)" />
                                <q-icon :color="point.visibility ? 'orange' : 'grey'" style="cursor: pointer;"
                                    :name="point.visibility ? 'visibility' : 'visibility_off'"
                                    @click.stop="toggleVisibility(point)">
                                    <q-tooltip>{{ point.visibility ? 'Masquer ce point dans l\'exposition' : 'Afficher ce point dans l\'exposition' }}</q-tooltip>
                                </q-icon>
                            </q-item-section>
                        </q-item>
                    </q-list>
                </div>
                <div>
                    <h4><q-icon name="content_copy" />Cloner les points</h4>
                    <q-list dense bordered padding class="rounded-borders">
                        <q-item v-for="point in pointsFromExhibit" :key="point.id">
                            <q-item-section style="padding: 0px; border: none ;">
                                {{ point.name }} - ( {{ point.title }} )
                            </q-item-section>
                            <q-item-section
                                style="display: flex; flex-direction: row; justify-content: end; padding: 0px; border: none ;"
                                avatar>
                                <q-icon color="blue" style="cursor: pointer;" name="control_point_duplicate"
                                    @click="duplicatePoint(point)" />
                                <q-icon color="red" style="cursor: pointer;" name="delete"
                                    @click="deleteDuplicatePoint(point.id)" />
                            </q-item-section>
                        </q-item>
                    </q-list>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { urlExposition } from '@/utils/urlExposition';
import Header from "../components/Header.vue";
import { onMounted, onUnmounted, ref, computed, watch, nextTick } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";
import axios from "axios";
import { useRouter } from "vue-router";
import MediaManager from "../components/MediaManager.vue";
import FileSelect from "../components/FileSelect.vue";
import WorkflowBreadcrumb from "../components/WorkflowBreadcrumb.vue";
import { usePointsCanvas } from "../composables/usePointsCanvas";
import PointEditorPanel from "../components/PointEditorPanel.vue";
export default {
    components: { Header, FileSelect, MediaManager, WorkflowBreadcrumb, PointEditorPanel },

    setup() {
        const store = useStore();
        const router = useRouter();
        const $q = useQuasar();
        const name = computed(() => store.state.exhibitName);
        let model = ref();
        let options = ref([]);
        let imgUrl = ref();
        let success = ref(false);
        let mainResourceId = ref();
        let resourcesFromPoints = ref();
        let mainResource = ref();
        let points = ref();
        let loading = ref(false);
        let resourcesList = ref(false);
        let saveError = ref(false);
        let uploading = ref(false);
        let progress = ref(0);
        let hasTitle = ref(true);
        let pointsFromExhibit = ref();
        let showFirstColumn = ref(true);
        let isDragging = ref(false);
        let selectedFiles = ref(null);
        let filesToUpload = ref([]);
        let uploadCurrent = ref(0);
        let uploadTotal = ref(0);

        let selectedPointId = ref(null);
        let panelDirty = ref(false);
        const selectedPoint = computed(() => (points.value || []).find(p => p.id === selectedPointId.value) || null);

        // Cache des URLs de ressources : évite un re-fetch à chaque aperçu.
        // Les URLs S3 signées expirent après 1 h → TTL de 45 min, et une
        // requête échouée est évincée pour être retentée.
        const RESOURCE_URL_TTL = 45 * 60 * 1000;
        const resourceUrlCache = new Map();
        const getUrlFromResourceId = async (resourceId) => {
            const entry = resourceUrlCache.get(resourceId);
            if (!entry || Date.now() - entry.at > RESOURCE_URL_TTL) {
                const promise = getFileFromResourceId(resourceId);
                promise.catch(() => resourceUrlCache.delete(resourceId));
                resourceUrlCache.set(resourceId, { promise, at: Date.now() });
            }
            return await resourceUrlCache.get(resourceId).promise;
        };

        const getFileFromResourceId = async (resourceId) => {
            console.log("get file from resource id", resourceId)
            let resource = options.value.filter(option => option.resource_id == resourceId || option.id == resourceId);
            console.log('resource', resource)
            if (!resource[0] || !resource[0].name) {
                throw new Error(`Resource not found for id ${resourceId}`);
            }
            let name = resource[0].name;
            let url = `${process.env.VUE_APP_BASEURL}/s3Resource/${name}`;
            let response = await axios.get(url);
            return response.data
        };

        const canvas = usePointsCanvas({
            getResourceUrl: getUrlFromResourceId,
            onPointClick: (id) => trySelect(id),
            onPointDragEnd: (id, pos) => savePointPosition(id, pos),
            onBackgroundClick: (pos) => {
                if (pos === null) { trySelect(null); return; }
                createPointAt(pos);
            },
            onPointHover: () => {},
        });

        const doSelect = (id) => {
            selectedPointId.value = id;
            panelDirty.value = false;
            canvas.selectPoint(id);
        };

        // Dialog partagé « modifications non enregistrées » : annule l'aperçu
        // live puis exécute l'action si l'utilisateur ignore ses modifs.
        const confirmDiscardChanges = (onConfirm) => {
            $q.dialog({
                title: "Modifications non enregistrées",
                message: `Le point « ${selectedPoint.value ? selectedPoint.value.name : ""} » a des modifications non enregistrées.`,
                ok: { label: "Ignorer les modifications", color: "red" },
                cancel: { label: "Rester sur ce point", color: "black", flat: true },
            }).onOk(() => {
                if (selectedPoint.value) canvas.updatePoint(selectedPoint.value);
                onConfirm();
            });
        };

        // Sélection avec garde-fou « modifications non enregistrées »
        const trySelect = (id) => {
            if (id === selectedPointId.value) return;
            if (selectedPointId.value && panelDirty.value) {
                confirmDiscardChanges(() => doSelect(id));
                return;
            }
            doSelect(id);
        };

        // Si le point sélectionné disparaît (suppression via la liste, re-fetch…),
        // réinitialiser la sélection pour ne pas garder d'état fantôme.
        watch(selectedPoint, (p) => {
            if (!p && selectedPointId.value) doSelect(null);
        });

        // PUT /point/:id écrase toutes les colonnes → toujours envoyer l'état complet.
        const buildPutBody = (point) => ({
            newX: point.x,
            newY: point.y,
            newPointName: point.name,
            newSize: point.size,
            newColor: point.color,
            newAssociatedResource: point.resource_id,
            newImagePosition: point.image_position,
            newNote: point.note,
            newShape: point.shape || "circle",
            newHoverAnimation: point.hover_animation || false,
            newShapeImageId: point.shape === "image" ? (point.shape_image_id || null) : null,
            newLabelColor: point.label_color || "#ffffff",
            newLabelSize: point.label_size || 16,
            newLabelFont: point.label_font || "Arial",
        });

        const onPanelPreview = (localProps) => {
            if (!selectedPoint.value) return;
            canvas.updatePoint({ ...selectedPoint.value, ...localProps });
        };

        const onPanelSave = async (localProps) => {
            try {
                const pendingMove = pendingPositionSaves.get(selectedPointId.value);
                if (pendingMove) await pendingMove;
                const merged = { ...selectedPoint.value, ...localProps };
                await axios.put(`${process.env.VUE_APP_BASEURL}/point/${merged.id}`, buildPutBody(merged));
                await getPoints();
                await getPointsFromExhibit();
                panelDirty.value = false;
                if (selectedPoint.value) await canvas.updatePoint(selectedPoint.value);
                $q.notify({ type: "positive", message: "Point enregistré", position: "top", timeout: 1500 });
                lastCreatedDefaults.value = {
                    size: merged.size,
                    color: merged.color,
                    shape: merged.shape,
                    shape_image_id: merged.shape_image_id,
                    label_color: merged.label_color,
                    label_size: merged.label_size,
                    label_font: merged.label_font,
                };
            } catch (error) {
                console.log(error);
                $q.notify({ type: "negative", message: "Erreur d'enregistrement", position: "top", timeout: 2500 });
            }
        };

        const onPanelCancel = () => {
            if (selectedPoint.value) canvas.updatePoint(selectedPoint.value); // revert visuel
            doSelect(null);
        };

        // Une sauvegarde de position par point à la fois : onPanelSave attend
        // la fin d'un PUT de position en vol pour ne pas écraser la nouvelle
        // position (les deux PUT écrivent toutes les colonnes).
        const pendingPositionSaves = new Map();

        const savePointPosition = async (id, pos) => {
            const point = (points.value || []).find(p => p.id === id);
            if (!point) return;
            const save = (async () => {
                try {
                    await axios.put(`${process.env.VUE_APP_BASEURL}/point/${id}`, buildPutBody({ ...point, x: pos.x, y: pos.y }));
                    point.x = pos.x;
                    point.y = pos.y;
                    $q.notify({ type: "positive", message: "Position enregistrée", position: "top", timeout: 1200 });
                } catch (error) {
                    console.log(error);
                    $q.notify({ type: "negative", message: "Erreur d'enregistrement de la position", position: "top", timeout: 2500 });
                    canvas.movePoint(id, point.x, point.y); // retour à la dernière position sauvegardée
                }
            })();
            pendingPositionSaves.set(id, save);
            try {
                await save;
            } finally {
                if (pendingPositionSaves.get(id) === save) pendingPositionSaves.delete(id);
            }
        };

        const confirmDeletePoint = (point) => {
            if (!point) return;
            $q.dialog({
                title: "Supprimer le point",
                message: `Supprimer le point « ${point.name} » ? Cette action est définitive.`,
                ok: { label: "Supprimer", color: "red" },
                cancel: { label: "Annuler", color: "black", flat: true },
            }).onOk(async () => {
                try {
                    await axios.delete(`${process.env.VUE_APP_BASEURL}/point/${point.id}`);
                    if (selectedPointId.value === point.id) doSelect(null);
                    canvas.removePoint(point.id);
                    await getPoints();
                    await getPointsFromExhibit();
                    await getResourcesFromPoints();
                    $q.notify({ type: "positive", message: "Point supprimé", position: "top", timeout: 1500 });
                } catch (error) {
                    console.log(error);
                    $q.notify({ type: "negative", message: "Erreur lors de la suppression", position: "top", timeout: 2500 });
                }
            });
        };

        let addMode = ref(false);
        // Défauts hérités du dernier point créé/enregistré (série de points semblables)
        let lastCreatedDefaults = ref(null);

        const enterAddMode = () => {
            addMode.value = true;
            canvas.setAddMode(true);
        };

        const toggleAddMode = () => {
            if (addMode.value) {
                addMode.value = false;
                canvas.setAddMode(false);
                return;
            }
            // Entrer en mode placement abandonne l'édition en cours : mêmes
            // garde-fous que le changement de sélection.
            if (selectedPointId.value && panelDirty.value) {
                confirmDiscardChanges(() => {
                    doSelect(null);
                    enterAddMode();
                });
                return;
            }
            enterAddMode();
        };

        const createPointAt = async ({ x, y }) => {
            if (!model.value || !mainResourceId.value || model.value.id !== mainResourceId.value) {
                addMode.value = false;
                canvas.setAddMode(false);
                $q.notify({ type: "warning", message: "Enregistrez d'abord cette image comme image principale", position: "top", timeout: 2500 });
                return;
            }
            addMode.value = false;
            canvas.setAddMode(false);
            try {
                loading.value = true;
                const d = lastCreatedDefaults.value || {};
                const postObj = {
                    x,
                    y,
                    resourceId: model.value.id,
                    exhibitId: store.state.exhibitId,
                    // le backend lit data.associatedResource.resource_id : "" → null en base
                    associatedResource: "",
                    imagePosition: "",
                    size: d.size || 10,
                    color: d.color || "black",
                    pointName: `Point ${(points.value ? points.value.length : 0) + 1}`,
                    pointNote: "",
                    shape: d.shape || "circle",
                    hoverAnimation: false,
                    shapeImageId: d.shape === "image" ? (d.shape_image_id || null) : null,
                    labelColor: d.label_color || "#ffffff",
                    labelSize: d.label_size || 16,
                    labelFont: d.label_font || "Arial",
                };
                const prevIds = new Set((points.value || []).map(p => p.id));
                await axios.post(`${process.env.VUE_APP_BASEURL}/point`, { data: postObj });
                // POST /point ne renvoie pas l'id → diff avant/après pour le retrouver
                await getPoints();
                await getPointsFromExhibit();
                const newPoint = (points.value || []).find(p => !prevIds.has(p.id));
                if (newPoint) {
                    try {
                        await canvas.updatePoint(newPoint);
                    } catch (renderError) {
                        console.log(renderError);
                    }
                    doSelect(newPoint.id);
                    $q.notify({ type: "positive", message: "Point créé — ajustez ses propriétés dans le panneau", position: "top", timeout: 2000 });
                } else {
                    // POST réussi mais point introuvable au re-fetch (échec réseau,
                    // image principale non sauvegardée…) : ne pas mentir à l'utilisateur.
                    $q.notify({ type: "warning", message: "Point créé, mais impossible de l'afficher — rechargez la page", position: "top", timeout: 3000 });
                }
            } catch (error) {
                console.log(error);
                $q.notify({ type: "negative", message: "Erreur lors de la création du point", position: "top", timeout: 2500 });
            } finally {
                loading.value = false;
            }
        };

        const stageWidth = () => (showFirstColumn.value ? 500 : 800);

        const toggleFirstColumn = async () => {
            showFirstColumn.value = !showFirstColumn.value;
            if (!imgUrl.value) return;
            try {
                await nextTick();
                await canvas.initStage({ containerId: "imageContainer", imgUrl: imgUrl.value, maxWidth: stageWidth() });
                await canvas.renderPoints(points.value);
            } catch (error) {
                console.log(error);
            }
        };

        const start = async () => {
            await getResources();
            await getMainResource();
            if (mainResourceId.value) {
                model.value = mainResource.value;
                await handleImageChange();
                await getResourcesFromPoints();
            }
            await getPointsFromExhibit();
            await getExhibitInfos();
        };
        const getResources = async () => {
            try {
                let url = `${process.env.VUE_APP_BASEURL}/resources`;
                let body = {
                    exhibitId: store.state.exhibitId,
                };
                let response = await axios.post(url, body);
                let results = response.data;
                options.value = results;
                options.value = options.value.sort((a, b) => {
                    const titleA = a.title ? a.title.toUpperCase() : "";
                    const titleB = b.title ? b.title.toUpperCase() : "";
                    if (titleA < titleB) return -1;
                    if (titleA > titleB) return 1;
                    return 0;
                });
            } catch (error) {
                console.log(error);
            }
        };
        const getFile = async (name) => {
            let url = `${process.env.VUE_APP_BASEURL}/s3Resource/${name}`;
            let response = await axios.get(url);
            imgUrl.value = response.data;
        };

        const getMainResource = async () => {
            try {
                let url = `${process.env.VUE_APP_BASEURL}/mainresource/${store.state.exhibitId}`;
                let response = await axios.get(url);
                if (response.data[0]) {
                    mainResourceId.value = response.data[0].main_resource_id;
                    mainResource.value = response.data[0];
                }
            } catch (error) {
                console.log(error);
            }
        };

        /* function blobToData(blob) {
             return new Promise((resolve) => {
                 const reader = new FileReader();
                 reader.onloadend = () => resolve(reader.result);
                 reader.readAsDataURL(blob);
             });
         }*/

        const getPoints = async () => {
            // Expo neuve : tant que l'image principale n'a pas été enregistrée,
            // mainResourceId est undefined. Sans ce garde-fou on appelait
            // /points/undefined ; l'API tente alors un cast en entier, échoue, et
            // sa route ne renvoie jamais de réponse : la promesse axios ne se
            // résout pas, donc le `finally` de handleImageChange ne s'exécute pas
            // et le voile de chargement reste affiché indéfiniment.
            if (!mainResourceId.value) {
                points.value = [];
                return;
            }
            try {
                let url = `${process.env.VUE_APP_BASEURL}/points/${mainResourceId.value}`;
                let response = await axios.get(url);
                console.log(response.data);
                points.value = response.data.sort((a, b) => {
                    // Utilisation de toLowerCase pour une comparaison insensible à la casse
                    const nameA = a.name.toLowerCase();
                    const nameB = b.name.toLowerCase();

                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }
                    return 0;
                });

            } catch (error) {
                console.log(error)
            }
        }

        const getPointsFromExhibit = async () => {
            try {
                let url = `${process.env.VUE_APP_BASEURL}/pointsFromExhibit2/${store.state.exhibitId}`;
                let response = await axios.get(url);
                pointsFromExhibit.value = response.data.sort((a, b) => {
                    // Utilisation de toLowerCase pour une comparaison insensible à la casse
                    const nameA = a.name.toLowerCase();
                    const nameB = b.name.toLowerCase();

                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }

                    return 0;
                });

            } catch (error) {
                console.log(error);
            }
        }

        const deletePoint = (pointId) => {
            confirmDeletePoint((points.value || []).find(p => p.id === pointId));
        };

        const deleteDuplicatePoint = (pointId) => {
            confirmDeletePoint((pointsFromExhibit.value || []).find(p => p.id === pointId));
        };

        const getFileIcon = (name) => {
            if (!name) return 'insert_drive_file';
            const ext = name.split('.').pop().toLowerCase();
            if (['jpg','jpeg','png','gif','svg','webp','bmp','tiff'].includes(ext)) return 'image';
            if (['mp4','avi','mov','wmv','webm','mkv'].includes(ext)) return 'videocam';
            if (['mp3','wav','ogg','flac','aac'].includes(ext)) return 'audiotrack';
            if (['doc','docx'].includes(ext)) return 'description';
            return 'insert_drive_file';
        };

        const addFiles = (files) => {
            const MAX_SIZE = 10000000;
            for (const file of files) {
                if (file.size > MAX_SIZE) {
                    alert(`"${file.name}" est volumineux, le chargement peut être long.`);
                }
                filesToUpload.value.push({
                    file,
                    title: file.name.replace(/\.[^/.]+$/, ''),
                    description: '',
                    source: '',
                    source_url: '',
                });
            }
        };

        const onDrop = (event) => {
            isDragging.value = false;
            const files = event.dataTransfer.files;
            if (files.length > 0) addFiles(files);
        };

        const onFilesSelected = (files) => {
            if (files && files.length > 0) {
                addFiles(files);
                selectedFiles.value = null;
            }
        };

        const importFiles = async () => {
            if (filesToUpload.value.length === 0) return;
            try {
                uploading.value = true;
                loading.value = true;
                uploadTotal.value = filesToUpload.value.length;

                for (let i = 0; i < filesToUpload.value.length; i++) {
                    uploadCurrent.value = i + 1;
                    progress.value = 0;

                    const item = filesToUpload.value[i];
                    let formData = new FormData();
                    formData.append("file", item.file);
                    formData.append("name", item.file.name);
                    formData.append("title", item.title);
                    formData.append("description", item.description);
                    if (item.source) formData.append("source", item.source);
                    if (item.source_url) formData.append("source_url", item.source_url);
                    if (store.state.exhibitId) formData.append("exhibitId", store.state.exhibitId);
                    if (store.state.resourcesCount !== undefined) formData.append("index", store.state.resourcesCount);

                    let url = `${process.env.VUE_APP_BASEURL}/file`;
                    await axios.post(url, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        onUploadProgress: (progressEvent) => {
                            if (progressEvent.lengthComputable) {
                                progress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            }
                        }
                    });
                }

                uploading.value = false;
                loading.value = false;
                filesToUpload.value = [];
                success.value = true;
                setTimeout(() => {
                    success.value = false;
                }, 1500);
                await getResources();
            } catch (error) {
                console.log(error);
                saveError.value = true;
                uploading.value = false;
                setTimeout(() => {
                    saveError.value = false;
                    loading.value = false;
                }, 1500);
            }
        };

        const navigateToExhibits = () => {
            router.push("/admin");
        }

        const getResourcesFromPoints = async () => {
            try {
                let url = `${process.env.VUE_APP_BASEURL}/pointsFromMainResource/${mainResourceId.value}`;
                let response = await axios.get(url);
                var outputJSON = {};

                response.data.forEach(function (item) {
                    if (
                        !Object.prototype.hasOwnProperty.call(outputJSON, item.point_id)
                    ) {
                        outputJSON[item.point_id] = [];
                    }

                    outputJSON[item.point_id].push(item);
                });
                resourcesFromPoints.value = outputJSON;
            } catch (error) {
                console.log(error);
            }
        };

        const duplicatePoint = async (point) => {
            try {
                //Dupliquer du point
                let url = `${process.env.VUE_APP_BASEURL}/duplicatePoint/${point.id}`;
                let response = await axios.post(url);
                let newPointId = response.data.newPointId;

                //Dupliquer les associations du point
                let url3 = `${process.env.VUE_APP_BASEURL}/duplicatePointAssociations/${point.id}/${newPointId}`;
                await axios.post(url3);

                //Dupliquer les postions des resources associées au point
                let url4 = `${process.env.VUE_APP_BASEURL}/duplicatePointPositions/${point.id}/${newPointId}`;
                await axios.post(url4);

                //Dupliquer les associations pour les ressources du point
                let url5 = `${process.env.VUE_APP_BASEURL}/duplicateResourceAssociations/${point.id}/${newPointId}`;
                await axios.post(url5);

                //Enregistrement du nouveau point dans la nouvelle image
                let url2 = `${process.env.VUE_APP_BASEURL}/pointmainresource/${newPointId}/${model.value.id}`;
                await axios.post(url2);
                await getPoints();
                await getPointsFromExhibit();
                await canvas.renderPoints(points.value);
                $q.notify({ type: "positive", message: "Point cloné", position: "top", timeout: 1500 });
            } catch (error) {
                console.log(error);
                $q.notify({ type: "negative", message: "Erreur lors du clonage", position: "top", timeout: 2500 });
            }
        }

        const handleImageChange = async () => {
            try {
                loading.value = true;
                const response = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${model.value.name}`);
                imgUrl.value = response.data;
                await canvas.initStage({ containerId: "imageContainer", imgUrl: imgUrl.value, maxWidth: stageWidth() });
                await getPoints();
                await canvas.renderPoints(points.value);
            } catch (error) {
                console.log(error);
                $q.notify({ type: "negative", message: "Impossible de charger l'image principale", position: "top", timeout: 2500 });
            } finally {
                loading.value = false;
            }
        };

        const toggleVisibility = async (point) => {
            try {
                await axios.put(`${process.env.VUE_APP_BASEURL}/pointVisibility/${point.id}/${!point.visibility}`);
                point.visibility = !point.visibility;
            } catch (error) {
                console.log(error);
                $q.notify({ type: "negative", message: "Erreur de changement de visibilité", position: "top", timeout: 2500 });
            }
        }

        const saveMainResource = async () => {
            try {
                let postBody = {
                    exhibitId: store.state.exhibitId,
                    mainResourceId: model.value.id
                }
                let url = `${process.env.VUE_APP_BASEURL}/template6mainresource`;
                let response = await axios.post(url, postBody);
                if (response.status == 200) {
                    success.value = true
                    setTimeout(() => {
                        success.value = false;
                        start();
                    }, 1500);
                }
            } catch (error) {
                console.log(error);
                saveError.value = true;
                setTimeout(() => {
                    saveError.value = false;
                }, 1500)
            }
        }

        const navigateToResourceManagement = () => {
            router.push("/resourceManagement");
        }

        const navigateBack = () => {
            router.go(-1);
        }

        const openNewTab = () => {
            window.open(store.getters.exhibitUrl, "_blank");
        }

        const navigateToExpo_bu = () => {
            router.push({ path: `/expo/${store.state.exhibitId}` });
        }

        const getExhibitInfos = async () => {
            try {
                let url = `${process.env.VUE_APP_BASEURL}/exhibit/${store.state.exhibitId}`;
                let response = await axios.get(url);
                console.log("exhibit", response.data);
                store.commit('SET_EXHIBITNAME', response.data.exhibit_name);
                store.commit('SET_EXHIBITID', store.state.exhibitId);
                let exhibitTemplate = response.data.template_name || 'template_1';
                let exhibitUrl   = urlExposition(exhibitTemplate, store.state.exhibitId);
                store.commit('SET_EXHIBITURL', exhibitUrl);
            } catch (error) {
                console.log(error);
            }
        }

        const onKeydown = (e) => {
            if (e.defaultPrevented) return; // un overlay Quasar (dialog, menu) a déjà géré la touche
            if (document.querySelector(".q-dialog, .q-menu")) return; // un overlay Quasar est ouvert : Échap lui appartient
            const active = document.activeElement;
            const tag = (active && active.tagName) || "";
            const typing = tag === "INPUT" || tag === "TEXTAREA"
                || !!(active && typeof active.closest === "function" && active.closest(".point-editor"));
            if (e.key === "Escape") {
                if (addMode.value) toggleAddMode();
                else if (selectedPointId.value && !typing) trySelect(null);
            }
            if ((e.key === "Delete" || e.key === "Backspace") && !typing && selectedPoint.value) {
                e.preventDefault();
                confirmDeletePoint(selectedPoint.value);
            }
        };
        onMounted(() => window.addEventListener("keydown", onKeydown));
        onUnmounted(() => window.removeEventListener("keydown", onKeydown));

        onMounted(start);
        onUnmounted(() => canvas.destroy());
        return {
            canvas,
            name,
            model,
            options,
            imgUrl,
            success,
            mainResourceId,
            resourcesFromPoints,
            mainResource,
            points,
            loading,
            resourcesList,
            saveError,
            selectedPointId,
            selectedPoint,
            panelDirty,
            trySelect,
            onPanelSave,
            onPanelCancel,
            onPanelPreview,
            addMode,
            toggleAddMode,
            uploading,
            progress,
            hasTitle,
            pointsFromExhibit,
            getResources,
            getFile,
            handleImageChange,
            getMainResource,
            getResourcesFromPoints,
            saveMainResource,
            deletePoint,
            confirmDeletePoint,
            navigateToResourceManagement,
            navigateBack,
            openNewTab,
            navigateToExpo_bu,
            navigateToExhibits,
            toggleVisibility,
            duplicatePoint,
            deleteDuplicatePoint,
            showFirstColumn,
            toggleFirstColumn,
            isDragging,
            selectedFiles,
            filesToUpload,
            uploadCurrent,
            uploadTotal,
            getFileIcon,
            onDrop,
            onFilesSelected,
            importFiles
        };
    },
};
</script>

<style scoped>
.nav-section {
    margin-bottom: 15px;
}

.back-btn {
    margin-left: 7%;
}

.container-section {
    flex-basis: 40%;
}

.hr-bottom {
    margin-bottom: 15px;
}

.form-section {
    flex-basis: 35%;
}

.send-btn {
    margin-left: 20%;
    margin-top: 20px;
}

.select-section {
    margin-left: 5px;
    display: flex;
    justify-content: start;
}

.save-section {
    text-align: right;
    margin-right: 5%;
    margin-bottom: -10%;
}

.main-section {
    display: flex;
    justify-content: space-evenly;
}

.add-circle-section {
    width: 30%;
}

.back-btn {
    margin-left: 7%;
}

.menu {
    margin: 1%;
    display: flex;
    justify-content: space-between;
}

.container {
    display: flex;
    justify-content: center;
}

.grid-container {
    display: grid;
    grid-template-columns: auto 1fr 1fr 1fr;
    gap: 10px;
    padding: 10px;
    position: relative;
    transition: all 0.3s ease;
}

.image-header-section {
    display: flex;
    align-items: flex-start;
    gap: 15px;
    margin-bottom: 20px;
}

.toggle-column {
    position: relative;
    z-index: 10;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    padding-top: 8px;
}

.toggle-btn {
    font-size: 14px;
    font-weight: bold;
    min-width: 35px;
    padding: 5px 10px;
    transition: all 0.3s ease;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.toggle-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.toggle-btn.collapsed {
    opacity: 0.8;
}

.image-title {
    margin: 1.33em 0;
    transition: all 0.3s ease;
    flex: 1;
    line-height: 1.2;
}

.image-title.title-expanded {
    transform: translateX(-20px);
}

.column {
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 10px;
    background-color: white;
    transition: all 0.3s ease;
}

.column h4 {
    font-size: 20px;
    font-weight: 600;
}

.column-1 {
    grid-column: 2;
    width: 100%;
}

.column-1.hidden {
    display: none;
}

.column-2 {
    grid-column: 3;
    width: 100%;
    /* L'image principale suit le scroll : elle reste visible pendant qu'on
       fait défiler les contrôles (colonne 3, plus longue). align-self: start
       empêche la cellule de s'étirer sur toute la hauteur de la rangée, ce qui
       laisse au sticky la marge nécessaire pour se déplacer. */
    position: sticky;
    top: 16px;
    align-self: start;
}

.column-2.expanded {
    grid-column: 2 / 4;
}

.column-3 {
    grid-column: 4;
    width: 100%;
}

.navButtons {
    display: flex;
    justify-content: space-between;
}

.banner {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 99999;

    /* Design */
    padding: 15px 30px;
    border-radius: 5px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    /* Typographie */
    font-size: 16px;
    font-family: 'Helvetica', sans-serif;
    /* Remplacez par la police de votre choix */

    /* Transition */
    transition: all 0.3s ease-in-out;
}

.infos {
    font-size: 16px;
    border: 1px solid gray;
    width: 70%;
    margin: auto;
    margin-bottom: 5px;
    border-radius: 5px;
}

.title {
    display: flex;
    align-items: center;
    font-size: 1.2 rem;
    margin-left: 1%;
}

.loader {
    position: fixed;
    /* ou "absolute" selon le besoin */
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background-color: rgba(255, 255, 255, 0.8);
    /* Un fond blanc semi-transparent */
}

.fixed-layout {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    /* espace entre les éléments */
}

.flex-column {
    max-width: 600px;
    /* Ajoutez éventuellement une marge si vous voulez espacer les colonnes */
    margin: 0 10px;
}
.column-toggle-container {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
}

.drop-zone {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  transition: border-color 0.2s, background-color 0.2s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.drop-zone--active {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.06);
}
</style>
